/**
 * OnayKapisi.js — firma girişinin onay GEÇİDİ (backlog #540, 2026-10-23): AuthProtected, personel olmayan her giriş için sayfayı
 * bunun içine koyar. Katılımcı ve Dekoratör girişleri, sunucunun /pending'ine düşen metinleri (KVKK + fuar kuralları) onaylamadan
 * portalın hiçbir sayfasını göremez; kural ve durum Components/Contexts/OnayContext.js'te, kimin sorulduğu services/onayGecidi.js'te.
 *
 * Ekran, Fuar Kuralları sayfasının iki satırı ve iki penceresidir (Components/Common/OnayMetniPencereleri.jsx): KVKK ayrı, kalan
 * metinler tek pencerede ve tek onayla. Metinler /pending'le zaten yüklüdür, pencere açılınca bir daha okunmaz. Onaylanan satır
 * "Onaylandı" kalır; son satır da onaylanınca sunucu boş dediği an portal açılır. İsteğe bağlı metinler "Şimdilik atla" ile geçilir;
 * zorunlu bir metin kaldıkça atla yoktur. Okunamayan liste geçidi AÇMAZ: hata ve "tekrar dene" gösterilir.
 * Üst çubuk (dil, profil/çıkış) açıktır: onaylamak istemeyen oradan çıkar.
 */
import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, CardFooter, CardHeader, Container, Spinner } from 'reactstrap';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import VerticalLayout from '../Layouts/index';
import { useOnay } from '../Components/Contexts/OnayContext';
import { KuralSatiri, OnayPenceresi, PENCERELER, pencereGruplari } from '../Components/Common/OnayMetniPencereleri';

const Kutu = ({ children }) => (
  <VerticalLayout>
    <div className="page-content">
      <Container fluid>
        <Card className="border-0 shadow-sm mt-3">
          <CardBody className="text-center py-5">{children}</CardBody>
        </Card>
      </Container>
    </div>
  </VerticalLayout>
);

function Gecit({ onay }) {
  const { t } = useTranslation('portal');
  const gruplar = useMemo(() => pencereGruplari(onay.bekleyenler), [onay.bekleyenler]);
  const [acik, setAcik] = useState(null);
  // Bu geçit ziyaretinde onaylanıp sunucunun listesinden düşen pencereler: satır "Onaylandı" olarak kalır, kaybolmaz.
  const [onaylanan, setOnaylanan] = useState({});

  const satirlar = [...Object.values(onaylanan).filter((g) => !gruplar.some((x) => x.ad === g.ad)), ...gruplar]
    .sort((a, b) => (a.ad === 'kvkk' ? 0 : 1) - (b.ad === 'kvkk' ? 0 : 1));
  const onaylandiMi = (ad) => Boolean(onaylanan[ad]) && !gruplar.some((g) => g.ad === ad);
  const zorunluKaldi = gruplar.some((g) => g.zorunlu);

  const onayla = (g) => async (metinler) => {
    await onay.onayla(metinler);
    setOnaylanan((o) => ({ ...o, [g.ad]: g }));
    setAcik(null);
  };

  return (
    <VerticalLayout>
      <div className="page-content">
        <Container fluid>
          <Card data-testid="onay-gecidi" data-tour="onay-gecidi">
            <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
              <i className="bx bx-book-content fs-18 text-muted" />
              <h5 className="card-title mb-0 flex-grow-1">{t('onayKapisi.baslik')}</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted mb-3">{t('onayKapisi.aciklama')}</p>
              <div className="fuar-kurallari-liste">
                {satirlar.map((g) => (
                  <KuralSatiri key={g.ad} ikon={PENCERELER[g.ad].ikon} ad={t(PENCERELER[g.ad].baslikAnahtari)}
                    onaylandi={onaylandiMi(g.ad)} isteginBagli={!g.zorunlu} ac={() => setAcik(g.ad)} testId={`gecit-satir-${g.ad}`}
                    tour={`onay-${g.ad}`} />
                ))}
              </div>
            </CardBody>
            {!zorunluKaldi && (
              <CardFooter className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span className="small text-muted">{t('onayKapisi.atlaAciklama')}</span>
                <Button color="light" onClick={onay.atla} data-testid="gecit-atla">{t('onayKapisi.simdilikAtla')}</Button>
              </CardFooter>
            )}
          </Card>

          {satirlar.map((g) => (
            <OnayPenceresi key={g.ad} acik={acik === g.ad} kapat={() => setAcik(null)} baslik={t(PENCERELER[g.ad].baslikAnahtari)}
              coklu={g.ad === 'kurallar'} dilId={onay.dilId} metinler={g.metinler}
              onaylandi={onaylandiMi(g.ad)} onayla={onayla(g)} />
          ))}
        </Container>
      </div>
    </VerticalLayout>
  );
}

/** @param children  geçit kapalıyken (ya da kalktığında) çizilecek sayfa */
export default function OnayKapisi({ children }) {
  const { t } = useTranslation('portal');
  const onay = useOnay();
  if (!onay || onay.durum === 'kapali' || onay.durum === 'temiz') return children;

  if (onay.durum === 'yukleniyor') {
    return (
      <Kutu>
        <Spinner color="primary" className="mb-2" />
        <h5 className="mb-0">{t('onayKapisi.yukleniyor')}</h5>
      </Kutu>
    );
  }
  if (onay.durum === 'hata') {
    return (
      <Kutu>
        <AlertTriangle size={40} className="text-danger mb-2" />
        <h5 className="mb-1">{t('onayKapisi.yuklenemedi')}</h5>
        <Alert color="danger" className="d-inline-block small mt-2">{onay.hata}</Alert>
        <div><Button color="primary" size="sm" onClick={onay.yenile}>{t('onayKapisi.tekrarDene')}</Button></div>
      </Kutu>
    );
  }
  return <Gecit onay={onay} />;
}
